import {
  Check,
  ChevronRight,
  ClipboardList,
  LockKeyhole,
  MessageCircleMore,
  Ruler,
} from "lucide-react";
import { Link } from "react-router";

export function ProjectProgressSidebar({ steps, progress }: { steps: string[]; progress: number }) {
  return (
    <aside className="hidden border-r border-stone-200 bg-stone-50 p-5 xl:block">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500">
        Project progress
      </p>
      <ol className="mt-5 space-y-1">
        {steps.map((step, index) => {
          const complete = index < progress;
          const current = index === progress;
          return (
            <li
              className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold ${
                current
                  ? "bg-white text-emerald-950 shadow-sm"
                  : complete
                    ? "text-emerald-800"
                    : "text-stone-400"
              }`}
              key={step}
            >
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs ${
                  complete
                    ? "bg-emerald-700 text-white"
                    : current
                      ? "bg-orange-100 text-orange-700"
                      : "border border-stone-300 bg-white"
                }`}
              >
                {complete ? <Check size={13} strokeWidth={3} /> : index + 1}
              </span>
              {step}
            </li>
          );
        })}
      </ol>
      <div className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
        <LockKeyhole className="text-emerald-700" size={20} />
        <p className="mt-3 text-sm font-bold text-emerald-950">Private while you plan</p>
        <p className="mt-1 text-xs leading-5 text-emerald-800">
          Your contractor sees the project only after you review and send it.
        </p>
      </div>
    </aside>
  );
}

export function ProjectContextSidebar({
  contractor,
  contractorSlug,
  projectId,
}: {
  contractor: { name: string; serviceArea: string | null } | null;
  contractorSlug: string;
  projectId: string;
}) {
  return (
    <aside className="hidden border-l border-stone-200 bg-stone-50 p-5 xl:block">
      <div className="flex items-center gap-2">
        <ClipboardList className="text-emerald-700" size={20} />
        <h2 className="font-bold text-emerald-950">Project context</h2>
      </div>
      <div className="mt-5 space-y-3">
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400">Contractor</p>
          <p className="mt-2 font-bold text-stone-800">{contractor?.name ?? "Loading…"}</p>
          {contractor?.serviceArea ? (
            <p className="mt-1 text-xs leading-5 text-stone-500">{contractor.serviceArea}</p>
          ) : null}
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="flex items-center gap-2 text-stone-800">
            <Ruler className="text-orange-600" size={18} />
            <p className="font-bold">Measurements</p>
          </div>
          <p className="mt-2 text-sm leading-6 text-stone-500">
            Room dimensions and exclusions appear in the detailed planner as they are collected.
          </p>
        </div>
        <Link
          className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-900 transition hover:bg-emerald-100"
          to={`/quote/${contractorSlug}`}
        >
          Review structured details <ChevronRight size={17} />
        </Link>
      </div>
      <div className="mt-8 border-t border-stone-200 pt-5 text-xs leading-5 text-stone-500">
        <p className="flex items-center gap-2 font-semibold text-stone-600">
          <MessageCircleMore size={16} /> Conversation linked to this project
        </p>
        {projectId ? <p className="mt-2">Draft {projectId.slice(0, 8)}</p> : null}
      </div>
    </aside>
  );
}
